import React, { useMemo, useState } from 'react';
import { Paper, Button, Alert, Stack } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import PseudoDocxDropzone from './PseudoDocxDropzone';
import { dePseudoAnonymizeDocxPII } from '../../services/piiService';

import PanelHeader from './PanelHeader';
import FileCard from '../shared/FileCard';
import DePseudoStatsPanel from './DePseudoStatsPanel';

const isDocx = (file) => (file?.name || '').toLowerCase().endsWith('.docx');

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'depseudonymized.docx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const DePseudoFileUploadPanel = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [fileItem, setFileItem] = useState(null);

  // ui state
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  // NEW: depseudo stats state
  const [depseudoStats, setDepseudoStats] = useState(null);

  const canStart = useMemo(() => {
    const hasFile = !!fileItem?.file && isDocx(fileItem.file);
    return hasFile && !isUploading;
  }, [fileItem, isUploading]);

  const canDownload = useMemo(() => fileItem?.status === 'success' && fileItem?.blob, [fileItem]);

  const resetMessages = () => {
    setError('');
    setInfoMessage('');
    setDepseudoStats(null);
  };

  const handleFilesAdded = (files) => {
    resetMessages();
    const f = files?.[0];
    if (!f) return;

    if (!isDocx(f)) {
      enqueueSnackbar(t('PseudoDocxDropzone.onlyDocx', 'Podporované sú iba .docx súbory.'), {
        variant: 'warning',
      });
      return;
    }

    setFileItem({
      id: crypto?.randomUUID?.() || String(Date.now()),
      name: f.name,
      file: f,
      status: 'ready',
      progress: 0,
      blob: null,
      downloadName: null,
      error: '',
    });
  };

  const handleRemove = () => {
    if (isUploading) return;
    setFileItem(null);
    resetMessages();
  };

  const handleStart = async () => {
    if (!canStart) return;

    resetMessages();
    setIsUploading(true);
    setFileItem((prev) => ({
      ...prev,
      status: 'uploading',
      progress: 30,
      error: '',
      blob: null,
      downloadName: null,
    }));

    try {
      const res = await dePseudoAnonymizeDocxPII(fileItem.file);

      if (!res.success) {
        setFileItem((prev) => ({
          ...prev,
          status: 'error',
          progress: 100,
          error: res.error || t('common.error', 'Chyba'),
        }));
        setError(res.error || t('common.error', 'Chyba'));
        return;
      }

      const { blob, filename, stats, replacedCount, mappingTotal } = res.data || {};

      setDepseudoStats(
        stats || {
          mappingTotal,
          replacedCount,
        }
      );

      setFileItem((prev) => ({
        ...prev,
        status: 'success',
        progress: 100,
        blob,
        downloadName: filename || `depseudonymized_${prev.name}`,
      }));

      setInfoMessage(t('DePseudoFileUploadPanel.success', 'Hotovo. Súbor bol de-pseudonymizovaný.'));
    } catch (e) {
      console.error(e);
      setFileItem((prev) => ({
        ...prev,
        status: 'error',
        progress: 100,
        error: e?.message || 'Failed',
      }));
      setError(e?.message || t('common.error', 'Chyba'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = () => {
    if (!canDownload) return;
    downloadBlob(fileItem.blob, fileItem.downloadName || fileItem.name);
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        borderRadius: 3, 
        border: '1px solid', 
        borderColor: 'divider', 
        bgcolor: 'background.paper',
        height: '100%'
      }}
    >
      <PanelHeader
        title={t('DePseudoFileUploadPanel.title', 'De-pseudonymizácia .docx')}
        onRefresh={resetMessages}
        loading={false}
        refreshTooltip={t('common.refresh', 'Obnoviť')}
      />

      <PseudoDocxDropzone disabled={isUploading} hasFile={!!fileItem} onFilesAdded={handleFilesAdded} />

      <FileCard 
        fileItem={fileItem} 
        isUploading={isUploading} 
        onDownload={handleDownload} 
        onRemove={handleRemove} 
        t={t} 
      />

      {/* NEW: integrity stats */}
      {fileItem?.status === 'success' && depseudoStats && <DePseudoStatsPanel stats={depseudoStats} t={t} />}

      {error && (
        <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {infoMessage && (
        <Alert severity="success" sx={{ mb: 1, borderRadius: 2 }}>
          {infoMessage}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
        <Button 
          variant="contained" 
          color="primary" 
          fullWidth 
          disableElevation
          disabled={!canStart} 
          onClick={handleStart}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {isUploading
            ? t('DePseudoFileUploadPanel.processing', 'Spracovávam...')
            : t('DePseudoFileUploadPanel.start', 'Spustiť de-pseudonymizáciu')}
        </Button>

        <Button
          variant="outlined"
          fullWidth
          disabled={!canDownload}
          onClick={handleDownload}
          startIcon={<DownloadIcon />}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {t('DePseudoFileUploadPanel.download', 'Stiahnuť výsledok')}
        </Button>
      </Stack>
    </Paper>
  );
};

export default DePseudoFileUploadPanel;